'use client';

import { useSession } from '@shared/auth';
import { useFormatter, useTranslations } from 'next-intl';
import { useState } from 'react';
import type { EditableNavigationMenuItemProps, LinkNavigationMenuItemProps } from '../../NavigationMenu/components/NavigationMenuItem/types';
import { NavigationMenu } from '../../NavigationMenu/NavigationMenu';
import { AddressEditSheet } from './components/AddressEditSheet';
import { NameEditSheet } from './components/NameEditSheet';
import type { ProfileInfoProps } from './types';

function parseBirthDate(raw: string): Date | null {
  const isoString = raw.length === 8 ? `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}` : raw;
  const date = new Date(isoString);
  return isNaN(date.getTime()) ? null : date;
}

function useFormatBirthDate(raw: string | undefined): string {
  const formatter = useFormatter();

  if (!raw) return '';

  const date = parseBirthDate(raw);
  return date ? formatter.dateTime(date, { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
}

function formatFullName(firstName: string, middleName: string | undefined, lastName: string): string {
  const nameParts = [firstName, middleName, lastName].filter(Boolean);
  return nameParts.join(' ');
}

type AddressFields = {
  streetName?: string | null;
  streetNumber?: string | null;
  area?: string | null;
  city?: string | null;
  postCode?: string | null;
};

function formatProfileDisplayAddress({ streetName, streetNumber, area, city, postCode }: AddressFields): string {
  const streetLine = [streetNumber, streetName]
    .map(value => value?.trim() ?? '')
    .filter(Boolean)
    .join(' ');

  return [streetLine, area?.trim(), city?.trim(), postCode?.trim()].filter(Boolean).join(', ');
}

function ProfileInfo({ className, namespace }: ProfileInfoProps) {
  const { session } = useSession();
  const playerProfile = session?.profile;
  const t = useTranslations(namespace);
  const [isNameEditOpen, setIsNameEditOpen] = useState(false);
  const [isAddressEditOpen, setIsAddressEditOpen] = useState(false);

  const displayName = playerProfile ? formatFullName(playerProfile.firstName, undefined, playerProfile.lastName) : '';
  const displayDateOfBirth = useFormatBirthDate(playerProfile?.birthDate);
  const addressText = playerProfile ? formatProfileDisplayAddress(playerProfile) : '';

  const items: (EditableNavigationMenuItemProps | LinkNavigationMenuItemProps)[] = [
    {
      label: t('name'),
      data: displayName,
      chevron: false,
      variant: 'editable',
      buttonText: t('edit'),
      onClick: () => setIsNameEditOpen(true),
      'data-track-name': 'Name',
      'data-track-privacy': 'Mask',
      'data-track-container': 'Name',
      isLoading: session.status === 'loading',
    },
    {
      label: t('date_of_birth'),
      data: displayDateOfBirth,
      chevron: false,
      variant: 'link',
      'data-track-name': 'DOB',
      'data-track-privacy': 'Mask',
      'data-track-container': 'DOB',
      isLoading: session.status === 'loading',
    },
    {
      label: t('address'),
      data: addressText,
      chevron: false,
      variant: 'editable',
      buttonText: t('edit'),
      onClick: () => setIsAddressEditOpen(true),
      'data-track-name': 'Address',
      'data-track-privacy': 'Mask',
      'data-track-container': 'Address',
      isLoading: session.status === 'loading',
    },
  ];

  return (
    <>
      <NavigationMenu variant="secondary" items={items} className={className} />
      <NameEditSheet open={isNameEditOpen} onOpenChange={setIsNameEditOpen} trigger={null} />
      <AddressEditSheet open={isAddressEditOpen} onOpenChange={setIsAddressEditOpen} trigger={null} />
    </>
  );
}

export { formatProfileDisplayAddress, ProfileInfo };
